import { MapPin, Navigation } from 'lucide-react'
import { type LocationDetail } from '@/api/accommodation/types'
import { useLocationRouteQuery } from '@/api/accommodation/hooks'
import { useUserLocation } from '@/contexts/useUserLocation'
import { Button } from '@/components/ui/button'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'

const PRAGUE = { latitude: 50.0755, longitude: 14.4378 }
const BRNO = { latitude: 49.1951, longitude: 16.6068 }

function formatDistance(metres: number) {
  const km = metres / 1000
  if (km < 1) return `${Math.round(metres)} m`
  if (km < 10) return `${km.toFixed(1)} km`
  return `${Math.round(km)} km`
}

function formatDuration(seconds: number) {
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  if (h === 0) return `${m} min`
  if (m === 0) return `${h} h`
  return `${h} h ${m} min`
}

function RouteRow({
  icon,
  label,
  distance,
  duration,
}: {
  icon: React.ReactNode
  label: string
  distance: number | undefined
  duration: number | undefined
}) {
  if (distance === undefined || duration === undefined) return null
  return (
    <>
      {icon}
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium">{formatDistance(distance)}</span>
      <span className="text-sm">{formatDuration(duration)}</span>
    </>
  )
}

function LocationCard({ location }: { location: LocationDetail }) {
  const { coords, permission, isRequesting, requestLocation } = useUserLocation()

  const pragueRoute = useLocationRouteQuery(location.slug, PRAGUE)
  const brnoRoute = useLocationRouteQuery(location.slug, BRNO)
  const userRoute = useLocationRouteQuery(location.slug, coords)

  return (
    <Accordion type="single" className="space-y-4" defaultValue="transport">
      <AccordionItem value="transport">
        <AccordionTrigger> {`${location.address.city}, ${location.region.name}`}</AccordionTrigger>
        <AccordionContent>
          <div className="mb-4 grid grid-cols-[1rem_4.5rem_auto_auto] items-center gap-x-2 gap-y-1.5">
            {userRoute.data ? (
              <RouteRow
                icon={<Navigation className="h-4 w-4 shrink-0 text-slate-400" />}
                label="Od vás"
                distance={userRoute.data.distance}
                duration={userRoute.data.duration}
              />
            ) : coords === null && permission !== 'denied' && permission !== 'unsupported' ? (
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  // requestLocation handles its own failures.
                  void requestLocation()
                }}
                disabled={isRequesting}
                className="col-span-4 h-auto justify-start gap-2 px-0 py-0 text-sm font-normal text-slate-500 has-[>svg]:px-0"
              >
                <Navigation className="h-4 w-4 shrink-0 text-slate-400" />
                {isRequesting ? 'Zjišťuji polohu…' : 'Spočítat vzdálenost od vás'}
              </Button>
            ) : null}
            <RouteRow
              icon={<MapPin className="h-4 w-4 shrink-0 text-slate-400" />}
              label="z Prahy"
              distance={pragueRoute.data?.distance}
              duration={pragueRoute.data?.duration}
            />
            <RouteRow
              icon={<MapPin className="h-4 w-4 shrink-0 text-slate-400" />}
              label="z Brna"
              distance={brnoRoute.data?.distance}
              duration={brnoRoute.data?.duration}
            />
          </div>
          <p className="mb-2 text-sm font-semibold text-slate-700">Parkování na místě</p>
          <ul className="ml-4 list-disc marker:text-sm">
            {location.description.transport
              .split('\n')
              .filter(Boolean)
              .map((item, key) => (
                <li key={key}>{item}</li>
              ))}
          </ul>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

export default LocationCard
